import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger,
} from "@/components/ui/select";
import { api, formatApiErrorDetail } from "@/lib/api";
import { buildOrderBOM } from "@/lib/orderBom";
import { toast } from "sonner";

export default function AddStockDialog({ open, onOpenChange, product, components, initialCfgId = "", initialDesignId = "", onDone }) {
  const configs = product?.pack_configs?.length ? product.pack_configs : [];
  const labelLine = (product?.lines || []).find((l) => /label/i.test(l.component_name || ""));
  const designs = labelLine?.designs || [];
  const [cfgId, setCfgId] = useState(() => (product?.pack_configs?.[0]?.id || ""));
  const [designId, setDesignId] = useState("");
  const [qty, setQty] = useState("");
  const [unit, setUnit] = useState("pallets");
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [consume, setConsume] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setCfgId(initialCfgId || product?.pack_configs?.[0]?.id || "");
      setDesignId(initialDesignId || "");
    }
  }, [open, initialCfgId, initialDesignId, product?.pack_configs]);

  const cfg = configs.find((c) => c.id === cfgId) || configs[0] || null;
  const cpp = Number(cfg?.cases_per_pallet) || 0;
  const casePackSize = Number(cfg?.bottles_per_case) || 0;
  const design = designs.find((d) => d.id === designId) || null;
  const qtyN = Number(qty) || 0;
  const cases = unit === "pallets" ? qtyN * cpp : qtyN;

  const submit = async () => {
    if (!cfg || !casePackSize) { toast.error("This product has no packing configuration."); return; }
    if (designs.length > 0 && !designId) { toast.error("Pick a design."); return; }
    if (cases <= 0) { toast.error("Enter a quantity."); return; }
    setSaving(true);
    try {
      let snapshot = [];
      if (consume) {
        const totalBottles = cases * casePackSize;
        const bpp = casePackSize * cpp;
        const totalPallets = bpp ? totalBottles / bpp : 0;
        const ppt = Number(cfg.pallets_per_truckload) || 0;
        snapshot = buildOrderBOM(product, components, {
          cfg, casePackSize, totalCases: cases, totalPallets,
          totalTrucks: ppt ? totalPallets / ppt : 0, designId,
        });
      }
      const { data } = await api.post("/finished-goods/manual-add", {
        product_id: product.id, config_id: cfg.id || "", design_id: designId,
        design_name: design?.name || "", cases, date,
        consume_components: consume, bom_snapshot: snapshot,
      });
      toast.success(`Added ${data.cases} cases to ${data.sku}${consume ? " — components deducted" : ""}. Find it under Finished Goods Inventory.`);
      onOpenChange(false);
      setQty("");
      onDone?.();
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); }
    setSaving(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="add-fg-stock-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Add Finished Goods — {product?.name}</DialogTitle>
          <DialogDescription className="text-left">Adds cases to Finished Goods Inventory (they land in Unassigned on the Shelf View).</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          {configs.length > 1 && (
            <Select value={cfg?.id || undefined} onValueChange={setCfgId}>
              <SelectTrigger className="rounded-sm" data-testid="add-fg-config">
                <span>{cfg?.label || `${cfg?.bottles_per_case} Pack`}</span>
              </SelectTrigger>
              <SelectContent>
                {configs.map((c) => (<SelectItem key={c.id} value={c.id}>{c.label || `${c.bottles_per_case} Pack`}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          {designs.length > 0 && (
            <Select value={designId || undefined} onValueChange={setDesignId}>
              <SelectTrigger className="rounded-sm" data-testid="add-fg-design">
                <span className={design ? "" : "text-slate-400"}>{design?.name || "Design"}</span>
              </SelectTrigger>
              <SelectContent>
                {designs.map((d) => (<SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>))}
              </SelectContent>
            </Select>
          )}
          <div className="flex gap-2">
            <Input type="text" inputMode="decimal" value={qty} onChange={(e) => setQty(e.target.value.replace(/[^0-9.]/g, ""))} placeholder="Qty" className="rounded-sm" data-testid="add-fg-qty" />
            <Select value={unit} onValueChange={setUnit}>
              <SelectTrigger className="w-32 rounded-sm capitalize" data-testid="add-fg-unit"><span>{unit}</span></SelectTrigger>
              <SelectContent>
                <SelectItem value="pallets" disabled={!cpp}>Pallets</SelectItem>
                <SelectItem value="cases">Cases</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {cases > 0 && casePackSize > 0 && (
            <p className="text-xs text-slate-500" data-testid="add-fg-summary">= {cpp ? `${(cases / cpp).toLocaleString(undefined, { maximumFractionDigits: 2 })} Pallets · ` : ""}{cases.toLocaleString()} Cases · {(cases * casePackSize).toLocaleString()} Units</p>
          )}
          <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-sm" data-testid="add-fg-date" />
          <div className="flex items-center justify-between rounded-sm border border-slate-200 p-3">
            <div>
              <p className="text-sm font-semibold text-slate-800">Consume components from inventory</p>
              <p className="text-[11px] text-slate-500">{consume ? "Deducts components per this BOM (can go negative)." : "Only adds finished goods — components untouched."}</p>
            </div>
            <Switch checked={consume} onCheckedChange={setConsume} data-testid="add-fg-consume-toggle" />
          </div>
        </div>
        <DialogFooter>
          <Button onClick={submit} disabled={saving} className="rounded-sm bg-[#002FA7] hover:bg-[#00218a]" data-testid="add-fg-save">
            {saving ? "Adding…" : "Add to stock"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
